/**
 * API calls for the rebuilt bulk send flow.
 *
 * Every path keeps its permission word (`fetch`/`add`/`send`/`update`/`delete`)
 * where the backend put it — authorisation substring-matches the URI, so a
 * tidier path 403s for every non-admin user (see `UrlConstants`).
 *
 * The endpoints are not deployed yet. This layer is deliberately thin so that
 * wiring it to the real server is a small change: callers get plain typed
 * objects and handle their own loading, error and empty states.
 */

import urls from "global/constants/UrlConstants";
import { setCoordinates } from "screens/Shared/CommonHelper";
import {
  getAuthFileCallParams,
  getCallParams,
  getDeleteCallParams,
  makeCall,
  makeCallFile,
} from "utils/service";
import {
  BulkBatch,
  BulkBatchDefinition,
  BulkBatchRow,
  BulkCreateResponse,
  BulkRecipientList,
  BulkRowStatus,
  BulkSendResponse,
  BulkSigningQueueEntry,
  BulkTemplateFromDocument,
} from "./BulkSendTypesAndValidation";

/**
 * Turn an uploaded, laid-out document into the hidden template a batch needs.
 * The template is absent from the template list and free of the plan's
 * template limit, so the sender never sees the word "template" on this path.
 */
/**
 * The batch's own hidden template, from the request on screen.
 *
 * The body goes through `setCoordinates` — the create-request flow's own
 * serialiser — rather than being hand-built. That is what maps the display
 * fields onto the ones the server actually reads (`unsignedDoc` →
 * `unsignedPdf`, `unsignedDocUrl` → `unsignedPdfUrl`), states `deliveryMode`
 * outright, splits every phone into `{ countryCode, number }` and resolves
 * field coordinates. Hand-building this payload is how `unsignedPdf` came to
 * be missing: Jackson has no alias for it, so it was dropped in silence and
 * the template saved with a file that had no name.
 */
export const createBulkTemplateFromDocument = async (
  signRequest: any,
  /**
   * Only for files that already live in the template folder — then the server
   * can point at them instead of copying. A freshly uploaded document leaves
   * this alone.
   */
  copyFiles?: false,
): Promise<BulkTemplateFromDocument> => {
  try {
    const query = copyFiles === false ? "?copyFiles=false" : "";
    const callParams = await getCallParams("POST", setCoordinates(signRequest));
    const response = await makeCall(
      `${urls.BULK_TEMPLATE_FROM_DOCUMENT}${query}`,
      callParams,
    );
    return response as BulkTemplateFromDocument;
  } catch (error: any) {
    throw error;
  }
};

/**
 * A template's signer slots, as both entry paths need them. The server resolves
 * `label` and `needsPhone` itself so the CSV's columns and its validation can
 * never disagree with what this screen showed.
 */
export const fetchBulkTemplateSlots = async (
  templateId: string,
): Promise<BulkTemplateFromDocument> => {
  try {
    const callParams = await getCallParams("GET");
    const response = await makeCall(
      `${urls.BULK_TEMPLATE_CSV}/${templateId}/slots`,
      callParams,
    );
    return response as BulkTemplateFromDocument;
  } catch (error: any) {
    throw error;
  }
};

/**
 * The blank CSV for a template — a header row and nothing else, with the
 * columns of every slot the batch has not pinned. Returned as a blob so the
 * caller can hand it straight to a download.
 */
export const fetchBulkCsvTemplate = async (
  templateId: string,
  fixedSignerIndexes: number[] = [],
): Promise<Blob> => {
  try {
    const query = fixedSignerIndexes.length
      ? `?fixedSigners=${fixedSignerIndexes.join(",")}`
      : "";
    const callParams = await getCallParams("GET");
    const response = await makeCallFile(
      `${urls.BULK_TEMPLATE_CSV}/${templateId}/csv${query}`,
      callParams,
    );
    return response as Blob;
  } catch (error: any) {
    throw error;
  }
};

/**
 * Create from rows typed in the grid. This only validates and saves a `Draft`:
 * the response carries the per-row report the sender works through before
 * anything is sent.
 */
export const createBulkBatch = async (
  definition: BulkBatchDefinition,
): Promise<BulkCreateResponse> => {
  try {
    const callParams = await getCallParams("POST", definition);
    const response = await makeCall(urls.BULK_BATCH_CREATE, callParams);
    return response as BulkCreateResponse;
  } catch (error: any) {
    throw error;
  }
};

/**
 * Create from an uploaded CSV. The file goes to the server — it is not parsed
 * in the browser — and a header the server doesn't recognise fails the whole
 * upload with a 400, while bad values come back as per-row issues.
 *
 * The parts are `file` and `definition`, matching the controller's own
 * `@RequestParam` names.
 */
export const createBulkBatchFromCsv = async (
  file: File,
  definition: BulkBatchDefinition,
): Promise<BulkCreateResponse> => {
  try {
    const formData = new FormData();
    formData.append("file", file);
    formData.append(
      "definition",
      new Blob([JSON.stringify(definition)], { type: "application/json" }),
    );
    const callParams = await getAuthFileCallParams(formData);
    const response = await makeCall(urls.BULK_BATCH_CREATE_CSV, callParams);
    return response as BulkCreateResponse;
  } catch (error: any) {
    throw error;
  }
};

/** Create from a list saved on an earlier send. */
export const createBulkBatchFromList = async (
  listId: string,
  definition: BulkBatchDefinition,
): Promise<BulkCreateResponse> => {
  try {
    const callParams = await getCallParams("POST", definition);
    const response = await makeCall(
      `${urls.BULK_BATCH_FROM_LIST}/${listId}`,
      callParams,
    );
    return response as BulkCreateResponse;
  } catch (error: any) {
    throw error;
  }
};

/**
 * Send the draft. Answers `202` with a queued count — a persisted fact to poll,
 * not a claim that anything has reached anyone yet.
 */
export const sendBulkBatch = async (
  batchId: string,
): Promise<BulkSendResponse> => {
  try {
    const callParams = await getCallParams("POST");
    const response = await makeCall(
      `${urls.BULK_BATCH_SEND}/${batchId}`,
      callParams,
    );
    return response as BulkSendResponse;
  } catch (error: any) {
    throw error;
  }
};

/** The batch and its live counts. Poll while it is `Queued` or `Running`. */
export const fetchBulkBatch = async (batchId: string): Promise<BulkBatch> => {
  try {
    const callParams = await getCallParams("GET");
    const response = await makeCall(
      `${urls.BULK_BATCH}/${batchId}`,
      callParams,
    );
    return response as BulkBatch;
  } catch (error: any) {
    throw error;
  }
};

export const fetchBulkBatches = async (
  pageNo: number = 1,
): Promise<BulkBatch[]> => {
  try {
    const callParams = await getCallParams("GET");
    const response = await makeCall(
      `${urls.BULK_BATCHES}/${pageNo}`,
      callParams,
    );
    return Array.isArray(response) ? (response as BulkBatch[]) : [];
  } catch (error: any) {
    throw error;
  }
};

export const fetchBulkBatchesCount = async (): Promise<number> => {
  try {
    const callParams = await getCallParams("GET");
    const response = await makeCall(urls.BULK_BATCHES_COUNT, callParams);
    return Number(response) || 0;
  } catch (error: any) {
    throw error;
  }
};

/** Rows, filterable by status. `Failed` is the view people actually want. */
export const fetchBulkBatchRows = async (
  batchId: string,
  pageNo: number = 1,
  status?: BulkRowStatus,
): Promise<BulkBatchRow[]> => {
  try {
    const query = status ? `?status=${status}` : "";
    const callParams = await getCallParams("GET");
    const response = await makeCall(
      `${urls.BULK_BATCH}/${batchId}/rows/pageNo/${pageNo}${query}`,
      callParams,
    );
    return Array.isArray(response) ? (response as BulkBatchRow[]) : [];
  } catch (error: any) {
    throw error;
  }
};

export const fetchBulkBatchRowsCount = async (
  batchId: string,
  status?: BulkRowStatus,
): Promise<number> => {
  try {
    const query = status ? `?status=${status}` : "";
    const callParams = await getCallParams("GET");
    const response = await makeCall(
      `${urls.BULK_BATCH}/${batchId}/rows/count${query}`,
      callParams,
    );
    return Number(response) || 0;
  } catch (error: any) {
    throw error;
  }
};

/** Requeue only the rows that failed. */
export const retryBulkBatch = async (batchId: string) => {
  try {
    const callParams = await getCallParams("POST");
    const response = await makeCall(
      `${urls.BULK_BATCH_SEND}/${batchId}/retry`,
      callParams,
    );
    return response;
  } catch (error: any) {
    throw error;
  }
};

/** Stop what hasn't gone out. Leaves delivered envelopes alone. */
export const cancelBulkBatch = async (batchId: string) => {
  try {
    const callParams = await getCallParams("POST");
    const response = await makeCall(
      `${urls.BULK_BATCH_UPDATE}/${batchId}/cancel`,
      callParams,
    );
    return response;
  } catch (error: any) {
    throw error;
  }
};

/** The results CSV, as a blob for download. */
export const exportBulkBatch = async (batchId: string): Promise<Blob> => {
  try {
    const callParams = await getCallParams("GET");
    const response = await makeCallFile(
      `${urls.BULK_BATCH}/${batchId}/export`,
      callParams,
    );
    return response as Blob;
  } catch (error: any) {
    throw error;
  }
};

/**
 * The envelopes in this batch waiting on the caller, oldest first — what a
 * fixed counter-signer works through instead of 500 separate emails.
 */
export const fetchBulkSigningQueue = async (
  batchId: string,
): Promise<BulkSigningQueueEntry[]> => {
  try {
    const callParams = await getCallParams("GET");
    const response = await makeCall(
      `${urls.BULK_BATCH}/${batchId}/signing-queue`,
      callParams,
    );
    return Array.isArray(response) ? (response as BulkSigningQueueEntry[]) : [];
  } catch (error: any) {
    throw error;
  }
};

export const saveBulkRecipientList = async (body: any) => {
  try {
    const callParams = await getCallParams("POST", body);
    const response = await makeCall(urls.BULK_LIST_ADD, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const fetchBulkRecipientLists = async (
  pageNo: number = 1,
): Promise<BulkRecipientList[]> => {
  try {
    const callParams = await getCallParams("GET");
    const response = await makeCall(`${urls.BULK_LISTS}/${pageNo}`, callParams);
    return Array.isArray(response) ? (response as BulkRecipientList[]) : [];
  } catch (error: any) {
    throw error;
  }
};

export const fetchBulkRecipientList = async (listId: string) => {
  try {
    const callParams = await getCallParams("GET");
    const response = await makeCall(`${urls.BULK_LIST}/${listId}`, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const deleteBulkRecipientList = async (listId: string) => {
  try {
    const callParams = await getDeleteCallParams();
    const response = await makeCall(
      `${urls.BULK_LIST_DELETE}/${listId}`,
      callParams,
    );
    return response;
  } catch (error: any) {
    throw error;
  }
};
